/**
 * Los «?» de Alexia (revisión de Alex, 15 oct 2026, «Los «?» de Alexia»).
 *
 * UNA sola fuente para lo que significa cada palabra de las pantallas: el «?»
 * junto a la palabra (web, components/QueEs.tsx) y Alexia, cuando le preguntan
 * «¿Qué es…?» (va en la parte fija del prompt, ver api/assistant/prompt.ts),
 * dicen exactamente lo mismo. Si una palabra sale en varias pantallas
 * (Vendiste, Materia prima, Te deja…), es UNA entrada y lleva siempre el mismo
 * texto.
 *
 * Los textos son los de Alex, copiados al pie de la letra: no se reescriben
 * aquí. Para cambiar uno, se cambia aquí y cambia en todas partes (también en
 * lo que contesta Alexia).
 *
 * Al pasar el ratón (o tocar en el teléfono) sale el texto, debajo el número
 * de esa persona si la pantalla lo da («Tú estás justo en el 60 %») y, al
 * final, «Pregúntale a Alexia», que abre el chat con «¿Qué es «…»?».
 */

export type TerminoGlosario =
  // En toda la app
  | 'todoSinIva'
  // Panel general, Ventas, Gastos, Estado de resultados
  | 'vendiste'
  | 'materiaPrima'
  | 'personal'
  | 'teDejo'
  | 'margenSeguridad'
  // Ingredientes
  | 'teSaleA'
  | 'seTiraAlLimpiar'
  // Recuento mensual
  | 'loQueGastaste'
  | 'gastasteDeVerdad'
  | 'segunTusRecetas'
  // Proveedores y Gastos
  | 'porPagar'
  // Tu carta, Preparaciones y Nuevo plato
  | 'costoConElQuePusisteElPrecio'
  | 'cuantoSubioElCosto'
  | 'teDeja'
  | 'teCuesta'
  | 'teCuestaTanda'
  | 'aproximado'
  | 'colchon'
  | 'comision'
  | 'rinde'
  // Ventas
  | 'cadaCliente'
  | 'porMesa'
  | 'cortesias'
  // Gastos
  | 'gastaste'
  | 'gastosFijos'
  | 'gastosVariables'
  | 'esperandoFactura'
  | 'sinFactura'
  | 'gastosGenerales'
  // Estado de resultados
  | 'primeCost'
  | 'desgaste'
  | 'intereses'
  | 'impuestos'
  | 'gananciaNeta'
  // Análisis
  | 'cuantoSeVende'
  | 'teDejaEnTotal'
  | 'puntoEquilibrio'
  // Inversiones
  | 'invertiste'
  | 'teCuestanCadaMes'
  | 'lasApuntasteComoGasto'
  | 'yaSeDesgasto'
  | 'leQuedaPorDesgastar'
  | 'teCostaraCadaMes'
  // Objetivos
  | 'teSeparaDelObjetivo'
  // Configuración
  | 'fijoOVariable';

export interface EntradaGlosario {
  /** La palabra tal como sale en la pantalla. */
  palabra: string;
  /** La etiqueta técnica, en pequeño y gris al lado («EBITDA», «prime cost»). */
  sigla?: string;
  /** Otras formas en que sale la misma palabra («Pendiente de pagar», «Colchón»). */
  tambien?: string[];
  /** El texto de Alex, literal. */
  texto: string;
}

export const GLOSARIO: Record<TerminoGlosario, EntradaGlosario> = {
  todoSinIva: {
    palabra: 'Todo sin IVA',
    texto: 'Todos los números van sin IVA: lo cobras y lo entregas a impuestos, no es tuyo.',
  },
  vendiste: {
    palabra: 'Vendiste',
    tambien: ['Vendiste, entre los 3'],
    texto: 'Lo que vendiste en estas fechas, sin IVA: local, para llevar y apps.',
  },
  materiaPrima: {
    palabra: 'Materia prima',
    texto: 'Lo que te costó la comida y la bebida que usaste: lo que tenías + lo que compraste − lo que te queda. Lo normal, entre el 28 y el 32 % de lo que vendes.',
  },
  personal: {
    palabra: 'Personal',
    texto: 'Lo que pagas por tu equipo: sueldos, prestaciones y cargas sociales. Lo sano, hasta el 30 % de lo que vendes.',
  },
  teDejo: {
    palabra: 'Te dejó el negocio',
    sigla: 'EBITDA',
    texto: 'Lo que te quedó después de pagar materia prima, personal y los gastos del local. Aún sin desgaste, intereses ni impuestos.',
  },
  margenSeguridad: {
    palabra: 'Cuánto pueden bajar tus ventas',
    texto: 'Lo que podrían bajar tus ventas antes de empezar a perder dinero. Si es 20 %, aunque vendas un 20 % menos, no pierdes.',
  },
  teSaleA: {
    palabra: 'Te sale a',
    texto: 'Precio por kilo, litro o pieza, para comparar proveedores.',
  },
  seTiraAlLimpiar: {
    palabra: 'Se tira al limpiar',
    texto: 'La parte que no llega al plato: cáscara, huesos, puntas, recortes.',
  },
  loQueGastaste: {
    palabra: 'Lo que gastaste',
    texto: 'Lo que tenías + lo que compraste − lo que te queda.',
  },
  gastasteDeVerdad: {
    palabra: 'Gastaste de verdad',
    texto: 'Lo que tenías + lo que compraste − lo que te queda, en % de lo que vendiste.',
  },
  segunTusRecetas: {
    palabra: 'Según tus recetas, deberías',
    texto: 'Lo que habrías gastado si cada plato vendido se hubiera hecho igual que su receta.',
  },
  porPagar: {
    palabra: 'Por pagar',
    tambien: ['Pendiente de pagar'],
    texto: 'Facturas que ya tienes y aún no pagaste.',
  },
  costoConElQuePusisteElPrecio: {
    palabra: 'Costo con el que pusiste el precio',
    texto: 'Lo que te costaba el plato la última vez que le pusiste precio. Si sube, te avisamos para que decidas.',
  },
  cuantoSubioElCosto: {
    palabra: 'Cuánto subió el costo',
    texto: 'Cuánto subió o bajó desde que pusiste el precio.',
  },
  teDeja: {
    palabra: 'Te deja',
    texto: 'Lo que te queda de cada plato después de ingredientes, empaque y comisión del canal. Sin personal ni renta.',
  },
  teCuesta: {
    palabra: 'Te cuesta',
    texto: 'Lo que te cuestan los ingredientes de una porción, con lo que se tira al limpiar y el colchón para imprevistos.',
  },
  teCuestaTanda: {
    palabra: 'Te cuesta',
    texto: 'Lo que te cuesta una tanda entera, contando lo que se tira al limpiar y el colchón para imprevistos.',
  },
  aproximado: {
    palabra: 'Aproximado',
    texto: 'Algún precio lo puso Alexia y aún no lo confirmaste.',
  },
  colchon: {
    palabra: 'Colchón para imprevistos',
    tambien: ['Colchón'],
    texto: 'Un extra para lo que no se mide: pequeñas subidas de precio, lo que se cae o se echa de más. Lo normal, 5–10 %.',
  },
  comision: {
    palabra: 'Comisión',
    texto: 'Lo que se queda el banco o la app de cada venta. Ponemos la habitual; cámbiala si tienes otra.',
  },
  rinde: {
    palabra: 'Rinde',
    tambien: ['Cuánto rinde'],
    texto: 'Lo que sale de una tanda: litros, kilos o piezas.',
  },
  cadaCliente: {
    palabra: 'Cada cliente gasta',
    texto: 'Lo vendido en el local entre los clientes que atendiste. Las apps no cuentan: un pedido no es un cliente sentado.',
  },
  porMesa: {
    palabra: 'Por mesa',
    texto: 'Lo vendido en el local entre las mesas que atendiste.',
  },
  cortesias: {
    palabra: 'Cortesías y descuentos',
    texto: 'Lo que regalaste o descontaste. No se resta de lo que vendiste: lo ves aparte para saber cuánto te cuesta.',
  },
  gastaste: {
    palabra: 'Gastaste',
    texto: 'Todo lo que apuntaste como gasto en estas fechas, sin IVA.',
  },
  gastosFijos: {
    palabra: 'Gastos fijos',
    texto: 'Los que pagas igual vendas o no: renta, internet, contador, seguros.',
  },
  gastosVariables: {
    palabra: 'Gastos variables',
    texto: 'Los que suben y bajan con lo que vendes: ingredientes, empaques, comisiones de las apps.',
  },
  esperandoFactura: {
    palabra: 'Esperando factura',
    texto: 'Notas o tickets que ya apuntaste y cuya factura te van a mandar o vas a sacar tú (en la web del súper, por ejemplo). Ya cuentan como gasto; cuando llega la factura, solo la juntas.',
  },
  sinFactura: {
    palabra: 'Sin factura',
    texto: 'Compras que nunca tendrán factura (en efectivo, en el mercado). Cuentan como gasto por su total.',
  },
  gastosGenerales: {
    palabra: 'Gastos generales',
    texto: 'Lo que pagas para tener el local abierto: renta, luz, agua, gas, seguros, contador, mantenimiento.',
  },
  primeCost: {
    palabra: 'Materia prima + personal',
    sigla: 'prime cost',
    texto: 'Tus dos gastos más grandes juntos. Lo sano: 60 % o menos de lo que vendes.',
  },
  desgaste: {
    palabra: 'Desgaste de equipos y obras',
    texto: 'Lo que pierden de valor cada mes tu horno, tu cámara o tu obra. Si no lo cuentas, tu ganancia sale más alta de lo que es.',
  },
  intereses: {
    palabra: 'Intereses de préstamos',
    texto: 'Solo los intereses; la parte del préstamo que devuelves no es gasto.',
  },
  impuestos: {
    palabra: 'Impuestos sobre la ganancia',
    texto: 'Lo que pagas de impuestos por lo que ganaste. No es el IVA.',
  },
  gananciaNeta: {
    palabra: 'Ganancia neta',
    texto: 'Lo que te queda de verdad, después de pagarlo todo: gastos, préstamos e impuestos.',
  },
  cuantoSeVende: {
    palabra: 'Cuánto se vende',
    texto: 'De cada 100 platos que vendes de este grupo, cuántos son este.',
  },
  teDejaEnTotal: {
    palabra: 'Te deja en total',
    texto: 'Lo que te deja cada plato × los que vendiste. Antes de personal y renta: no es tu ganancia.',
  },
  puntoEquilibrio: {
    palabra: '¿Cuánto necesitas vender?',
    texto: 'Lo mínimo que tienes que vender cada día que abres para no perder dinero. Sale de tus gastos fijos y de lo que te deja cada peso después de los gastos variables.',
  },
  invertiste: {
    palabra: 'Invertiste',
    texto: 'Lo que pagaste por cosas que vas a usar años (horno, mesas, la obra), sin IVA.',
  },
  teCuestanCadaMes: {
    palabra: 'Te cuestan cada mes',
    texto: 'Lo que pagaste, repartido en los años que dura. Un horno de 12.000 que dura 5 años te cuesta 200 al mes.',
  },
  lasApuntasteComoGasto: {
    palabra: 'Las apuntaste como gasto',
    texto: 'Compras que contaste enteras el mes que las pagaste, en vez de repartirlas en los años que duran.',
  },
  yaSeDesgasto: {
    palabra: 'Ya se desgastó',
    texto: 'Lo que ya contaste como desgaste desde que la compraste.',
  },
  leQuedaPorDesgastar: {
    palabra: 'Le queda por desgastar',
    texto: 'Lo que falta por contar en los meses que le quedan.',
  },
  teCostaraCadaMes: {
    palabra: 'Te costará cada mes',
    texto: 'Lo que pagaste, repartido en los años que dura. Es lo que sale cada mes como desgaste en tu Estado de resultados.',
  },
  teSeparaDelObjetivo: {
    palabra: 'Te falta',
    tambien: ['Te pasas'],
    texto: 'Cuánto te separa de tu objetivo.',
  },
  fijoOVariable: {
    palabra: 'Fijo o variable',
    texto: 'Fijo: lo pagas igual vendas o no (renta, internet). Variable: sube y baja con lo que vendes (ingredientes, empaques, comisiones).',
  },
};

/**
 * Lo que se añade al texto de Materia prima cuando el mes no tiene recuento
 * (Estado de resultados): sin lo que te queda, contamos lo que compraste.
 */
export const MATERIA_PRIMA_SIN_RECUENTO = 'Sin recuento, contamos lo que compraste.';

/** La pregunta que abre «Pregúntale a Alexia»: «¿Qué es «Materia prima»?». */
export function preguntaDeQueEs(palabra: string): string {
  return `¿Qué es «${palabra}»?`;
}

/**
 * El glosario como texto para el prompt de Alexia. Va en la parte FIJA del
 * prompt (cacheada): no depende del restaurante ni de la fecha, así que es
 * igual byte a byte en todas las llamadas.
 */
export function glosarioParaAlexia(): string {
  return Object.values(GLOSARIO)
    .map((e) => {
      const nombres = [e.palabra, ...(e.tambien ?? [])].map((p) => `«${p}»`).join(' / ');
      return `- ${nombres}${e.sigla ? ` (${e.sigla})` : ''}: ${e.texto}`;
    })
    .join('\n');
}

/**
 * La frase con el número de la persona para un porcentaje con objetivo.
 * `masEsMejor`: en «Te dejó el negocio» se cumple estando por encima; en los
 * costos, por debajo. `pct` ya formateado lo da la pantalla (formato del país).
 */
export function fraseTuPorcentaje(input: {
  pct: number;
  objetivo?: number | null;
  masEsMejor?: boolean;
  formato: (n: number) => string;
}): string {
  const { pct, objetivo, masEsMejor = false, formato } = input;
  const redondo = Math.round(pct);
  const tu = objetivo != null && redondo === Math.round(objetivo) ? `Tú estás justo en el ${formato(pct)}` : `Tú estás en el ${formato(pct)}`;
  if (objetivo == null) return `${tu}.`;
  const cumple = masEsMejor ? pct >= objetivo : pct <= objetivo;
  if (masEsMejor) return cumple ? `${tu}: cumples tu objetivo (${formato(objetivo)} o más).` : `${tu}; tu objetivo es ${formato(objetivo)} o más.`;
  return cumple ? `${tu}: dentro de tu objetivo (hasta ${formato(objetivo)}).` : `${tu}; tu objetivo es hasta ${formato(objetivo)}.`;
}
